import React, { useRef, useState } from 'react';
import api from '../api/axios';
import { FileText, Download, Trash2, UploadCloud, Box, Loader2, Eye } from 'lucide-react';
import IfcViewerModal from './IfcViewerModal';

interface DocMeta {
  id: string;
  nome: string;
  tipo?: string;
  size?: number;
  uploaded_at?: string;
}

interface IfcMeta {
  nome: string;
  size?: number;
  uploaded_at?: string;
}

interface ProjectDocumentsProps {
  projectId: string;
  documentos: DocMeta[];
  ifcFile: IfcMeta | null;
  canManage: boolean;
  onChange: () => void;
  onError?: (message: string) => void;
  show?: 'documents' | 'ifc' | 'both';
}

const formatSize = (bytes?: number): string => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Data por extenso (ex.: "5 de junho de 2026"), consistente com a página do projeto.
const formatDatePt = (d?: string): string => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return d; }
};

const ProjectDocuments: React.FC<ProjectDocumentsProps> = ({
  projectId,
  documentos,
  ifcFile,
  canManage,
  onChange,
  onError,
  show = 'both',
}) => {
  const docInputRef = useRef<HTMLInputElement>(null);
  const ifcInputRef = useRef<HTMLInputElement>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingIfc, setUploadingIfc] = useState(false);
  const [showViewer, setShowViewer] = useState(false);

  const fail = (msg: string) => onError?.(msg);

  // Download autenticado: o endpoint exige o header Authorization (injetado pelo `api`),
  // por isso descarregamos como blob e disparamos o download no browser.
  const download = async (url: string, filename: string) => {
    try {
      const { data } = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      fail('Erro ao transferir o ficheiro.');
    }
  };

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/projects/${projectId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange();
    } catch (err) {
      fail('Erro ao carregar o documento.');
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    try {
      await api.delete(`/projects/${projectId}/documents/${docId}`);
      onChange();
    } catch (err) {
      fail('Erro ao apagar o documento.');
    }
  };

  const handleIfcUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.ifc')) {
      fail('O ficheiro tem de ter extensão .ifc');
      if (ifcInputRef.current) ifcInputRef.current.value = '';
      return;
    }
    setUploadingIfc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/projects/${projectId}/ifc`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange();
    } catch (err) {
      fail('Erro ao carregar o ficheiro IFC.');
    } finally {
      setUploadingIfc(false);
      if (ifcInputRef.current) ifcInputRef.current.value = '';
    }
  };

  const handleDeleteIfc = async () => {
    try {
      await api.delete(`/projects/${projectId}/ifc`);
      onChange();
    } catch (err) {
      fail('Erro ao remover o ficheiro IFC.');
    }
  };

  // --- Documentos (estilo embebido, para dentro da caixa de informações) ---
  const docsSection = (
    <div>
      <div className="flex items-center justify-between mb-4 border-b border-slate-100 dark:border-slate-700 pb-3">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Documentos</h4>
        <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300">{documentos?.length || 0}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(documentos || []).map((doc) => (
          <div key={doc.id} className="flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 shrink-0">
                <FileText size={14} className="text-emerald-500" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{doc.nome}</p>
                <p className="text-xs text-slate-400">
                  {formatSize(doc.size)}{doc.uploaded_at ? ` · ${formatDatePt(doc.uploaded_at)}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => download(`/projects/${projectId}/documents/${doc.id}/download`, doc.nome)}
                className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                title="Transferir"
              >
                <Download size={15} />
              </button>
              {canManage && (
                <button
                  onClick={() => handleDeleteDoc(doc.id)}
                  className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Apagar"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        ))}

        {(!documentos || documentos.length === 0) && (
          <p className="col-span-full text-sm text-slate-400 py-1">Sem documentos.</p>
        )}
      </div>

      {canManage && (
        <>
          <button
            onClick={() => docInputRef.current?.click()}
            disabled={uploadingDoc}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-all disabled:opacity-50"
          >
            {uploadingDoc ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            <span>{uploadingDoc ? 'A carregar…' : 'Adicionar documento'}</span>
          </button>
          <input ref={docInputRef} type="file" className="hidden" onChange={handleDocUpload} />
        </>
      )}
    </div>
  );

  // --- Modelo IFC (cartão próprio, para a coluna lateral) ---
  const ifcSection = (
    <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
      <div className="flex items-center gap-2.5 mb-4 text-slate-500 dark:text-slate-300">
        <Box size={16} className="text-indigo-500" />
        <h4 className="text-sm font-semibold">Modelo IFC</h4>
      </div>

      {ifcFile ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2.5 bg-indigo-600 text-white rounded-xl shrink-0"><Box size={18} /></div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{ifcFile.nome}</p>
              <p className="text-xs text-slate-400">
                {formatSize(ifcFile.size)}{ifcFile.uploaded_at ? ` · ${formatDatePt(ifcFile.uploaded_at)}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowViewer(true)}
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-all active:scale-95"
          >
            <Eye size={16} /> <span>Ver modelo 3D</span>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => download(`/projects/${projectId}/ifc/download`, ifcFile.nome)}
              className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200 rounded-xl text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-all"
              title="Transferir"
            >
              <Download size={15} /> <span>Transferir</span>
            </button>
            {canManage && (
              <>
                <button
                  onClick={() => ifcInputRef.current?.click()}
                  disabled={uploadingIfc}
                  className="p-2.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 rounded-xl hover:text-indigo-600 transition-all disabled:opacity-50"
                  title="Substituir IFC"
                >
                  {uploadingIfc ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
                </button>
                <button
                  onClick={handleDeleteIfc}
                  className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  title="Remover IFC"
                >
                  <Trash2 size={15} />
                </button>
              </>
            )}
          </div>
        </div>
      ) : canManage ? (
        <button
          onClick={() => ifcInputRef.current?.click()}
          disabled={uploadingIfc}
          className="w-full border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-6 text-center hover:border-indigo-500/50 transition-all disabled:opacity-50"
        >
          <div className="w-10 h-10 bg-indigo-600/10 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-2">
            {uploadingIfc ? <Loader2 size={18} className="animate-spin" /> : <UploadCloud size={18} />}
          </div>
          <p className="text-sm font-medium text-slate-900 dark:text-white">{uploadingIfc ? 'A carregar…' : 'Carregar ficheiro IFC'}</p>
          <p className="text-xs text-slate-400 mt-0.5">Apenas .ifc</p>
        </button>
      ) : (
        <p className="text-sm text-slate-400">Sem modelo IFC.</p>
      )}
      <input ref={ifcInputRef} type="file" accept=".ifc" className="hidden" onChange={handleIfcUpload} />
    </div>
  );

  return (
    <>
      {(show === 'documents' || show === 'both') && docsSection}
      {(show === 'ifc' || show === 'both') && ifcSection}

      {showViewer && ifcFile && (
        <IfcViewerModal
          isOpen={showViewer}
          onClose={() => setShowViewer(false)}
          projectId={projectId}
          fileName={ifcFile.nome}
        />
      )}
    </>
  );
};

export default ProjectDocuments;
